import React, { useState } from 'react';

import camelCase from 'lodash/camelCase';
import { Formik } from 'formik';
import { useDebounce } from 'react-use';

import { Typography, DialogContent, DialogActions } from '@mui/material';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import TextField from 'src/components/ui/TextField';
import Icon from 'src/components/ui/Icon';
import Button from 'src/components/ui/Button';
import Avatar from 'src/components/ui/Avatar';
import Loader from 'src/components/ui/Loader';

import { isBottomDiv } from 'src/utils/isBottom';
import { colours } from 'src/constants/colours';

import type { Medium, CompanyMembersWithTotalQueryVariables, User } from 'src/types/__generated__/graphql';
import { useCompanyMembersWithTotalQuery } from 'src/hooks/__generated__/queries';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        position: relative;
        height: 70%;
    }
`;

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding: 1.5rem 1.5rem 0.5rem 1.5rem;
`;

const StyledDialogContent = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    padding: 0 1.5rem;
`;

const StyledDialogActions = styledComponent(DialogActions)`
    border-top: 0.0625rem solid ${colours.grey.G200};
    padding: 1.5rem;
`;

const UserWrapper = styledComponent(Box)<{
    $selected: boolean;
}>`
    padding: 0.5rem 1rem;
    border-radius: 0.25rem;
    ${({ $selected }) => $selected && `background-color: ${colours.secondary.O12};`}
`;

const ScrollContainer = styledTag('div')`
    overflow-y: scroll;
`;
const StyledAvatar = styledComponent(Avatar)`
    margin-right: 0.25rem;
    cursor: pointer;
`;

StyledDialog.defaultProps = {
    maxWidth: 'xs',
    fullWidth: true,
};

interface Props {
    title: string;
    submitLabel: string;
    variables: CompanyMembersWithTotalQueryVariables;
    onSelection: (selectedUser: Pick<User, 'id' | 'fullName'>) => void;
    open: boolean;
    onClose: () => void;
}

const SearchCompanyMembersModal: React.FC<Props> = ({ title, submitLabel, variables, onSelection, open, onClose }) => {
    const { t } = useTranslation(['company', 'common']);
    const defaultLimit = 10;
    const [allDataLoaded, setAllDataLoaded] = useState<boolean>(false);
    const [query, setQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useDebounce(() => setDebouncedQuery(query), 500, [query]);

    const {
        data: companyMembers,
        loading,
        fetchMore,
    } = useCompanyMembersWithTotalQuery({
        variables: {
            offset: 0,
            limit: defaultLimit,
            query: debouncedQuery.length > 0 ? debouncedQuery : null,
            orderBy: { 'user.firstName': 'ASC' },
            ...variables,
        },
        skip: !open,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            setAllDataLoaded(data.companyMembersWithTotal?.results?.length === data.companyMembersWithTotal?.total);
        },
    });

    const handleClose = () => {
        setSelectedUserId(undefined);
        setAllDataLoaded(false);
        setQuery('');
        onClose();
    };

    const handleChange = (ev: string) => {
        setQuery(ev);
        setAllDataLoaded(false);
    };

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        const actualCompanyMembersLength = companyMembers?.companyMembersWithTotal?.results?.length;
        if (bottom && !loading && !allDataLoaded) {
            await fetchMore({
                variables: {
                    offset: actualCompanyMembersLength,
                },
            });
        }
    };
    return (
        <StyledDialog open={open} fullWidth>
            <StyledDialogTitle onClose={handleClose}>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <Typography variant="h3">{title}</Typography>
                </Box>
            </StyledDialogTitle>
            <StyledDialogContent>
                {/* Formik is used here to avoid errors using useField in children components */}
                <Formik
                    initialValues={{
                        query: '',
                    }}
                    onSubmit={console.info}
                >
                    {() => (
                        <TextField
                            data-target-id="search-company-members-modal-input"
                            name="query"
                            placeholder={t('company:searchMember')}
                            InputProps={{
                                startAdornment: <Icon name="Search" color={colours.primary.O60} />,
                            }}
                            onChange={(e) => handleChange(e.target.value)}
                        />
                    )}
                </Formik>
                <ScrollContainer onScroll={handleScroll}>
                    {companyMembers?.companyMembersWithTotal?.results?.map(
                        (user) =>
                            user && (
                                <UserWrapper
                                    key={user.id}
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    width="100%"
                                    onClick={() => setSelectedUserId(user.id)}
                                    $selected={user.id === selectedUserId}
                                >
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <StyledAvatar
                                            alt={user.fullName}
                                            image={user.image as Medium}
                                            fallbackText={user.initials}
                                            size={36}
                                        />
                                        <Box ml="0.5rem">
                                            <Typography noWrap>{user.fullName}</Typography>
                                            <Typography variant="subtitle2">
                                                {t(`common:activities.${camelCase(user.activity?.labelKey)}`)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {user.id === selectedUserId ? (
                                        <Box display="flex" flexDirection="column" justifyContent="center">
                                            <Icon name="CheckMarkBlue" />
                                        </Box>
                                    ) : null}
                                </UserWrapper>
                            ),
                    )}
                    {loading && <Loader />}
                </ScrollContainer>
            </StyledDialogContent>
            <StyledDialogActions>
                <Box display="flex" flexDirection="row" justifyContent="end">
                    <Button variant="outlined" onClick={handleClose}>
                        {t('common:cancel')}
                    </Button>
                    <Box ml={0.5}>
                        <Button
                            onClick={() => {
                                const selectedUser = companyMembers?.companyMembersWithTotal?.results?.find(
                                    (user) => user?.id === selectedUserId,
                                );
                                if (selectedUser) {
                                    onSelection(selectedUser);
                                }
                                handleClose();
                            }}
                            disabled={!selectedUserId}
                        >
                            {submitLabel}
                        </Button>
                    </Box>
                </Box>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default SearchCompanyMembersModal;
